import { blue, orange, red, teal } from "@mui/material/colors";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import {
  AllSeriesType,
  BarSeriesType,
  LineSeriesType,
} from "@mui/x-charts-premium";
import { useNumberFormat } from "shared-ts-locale";

export const useSeries = (
  hideBurnHours: boolean,
  hidePerPeriod: boolean,
  displaySootIndex: boolean
): Array<AllSeriesType> => {
  const { t } = useTranslation("firefly-shared-ts-domain");
  const numberFormat = useNumberFormat();

  return useMemo(() => {
    const series: Array<AllSeriesType> = [];

    if (!hideBurnHours && !hidePerPeriod) {
      series.push({
        id: "burnHours",
        dataKey: "burnHours",
        type: "bar",
        label: t("Burn Hours"),
        color: teal["700"],
        yAxisId: "burnHours",
        valueFormatter: (v) => v?.format(numberFormat) ?? null,
      } as BarSeriesType);
    }

    if (!hidePerPeriod) {
      if (displaySootIndex) {
        series.push({
          id: "sootIndex",
          dataKey: "sootIndex",
          type: "bar",
          label: t("Soot Index"),
          color: orange["700"],
          yAxisId: "sootIndex",
          valueFormatter: (v) => v?.format(numberFormat) ?? null,
        } as BarSeriesType);
      } else {
        series.push({
          id: "sootingDegree",
          dataKey: "sootingDegree",
          type: "bar",
          label: t("Sooting Degree"),
          color: orange["700"],
          yAxisId: "sootingDegree",
          valueFormatter: roundedPercentageValueWithOneDecimalFormatter,
        } as BarSeriesType);
      }
    }

    if (!hideBurnHours) {
      series.push({
        id: "burnHoursCumulative",
        dataKey: "burnHoursCumulative",
        type: "line",
        label: `${t("Burn Hours")}, ${t("cumulative")}`,
        color: blue["500"],
        yAxisId: "burnHours",
        valueFormatter: (v) => v?.format(numberFormat) ?? null,
      } as LineSeriesType);
    }

    if (displaySootIndex) {
      series.push({
        id: "sootIndexCumulative",
        dataKey: "sootIndexCumulative",
        type: "line",
        label: `${t("Soot Index")}, ${t("cumulative")}`,
        yAxisId: "sootIndex",
        color: red["500"],
        valueFormatter: (v) => v?.format(numberFormat) ?? null,
      } as LineSeriesType);
    } else {
      series.push({
        id: "sootingDegreeCumulative",
        dataKey: "sootingDegreeCumulative",
        type: "line",
        label: `${t("Sooting Degree")}, ${t("cumulative")}`,
        yAxisId: "sootingDegree",
        color: red["500"],
        valueFormatter: roundedPercentageValueFormatter,
      } as LineSeriesType);
    }

    if (!hideBurnHours && !hidePerPeriod) {
      series.push({
        id: "predictedBurnHours",
        dataKey: "predictedBurnHours",
        type: "bar",
        label: t("Predicted Burn Hours"),
        color: teal["200"],
        yAxisId: "burnHours",
        valueFormatter: (v) => v?.format(numberFormat) ?? null,
      } as BarSeriesType);
    }

    if (!hidePerPeriod) {
      if (displaySootIndex) {
        series.push({
          id: "predictedSootIndex",
          dataKey: "predictedSootIndex",
          type: "bar",
          label: `${t("Predicted Soot Index")}`,
          color: orange["200"],
          yAxisId: "sootIndex",
          valueFormatter: (v) => v?.format(numberFormat) ?? null,
        } as BarSeriesType);
      } else {
        series.push({
          id: "predictedSootingDegree",
          dataKey: "predictedSootingDegree",
          type: "bar",
          label: `${t("Predicted Sooting Degree")}`,
          color: orange["200"],
          yAxisId: "sootingDegree",
          valueFormatter: roundedPercentageValueWithOneDecimalFormatter,
        } as BarSeriesType);
      }
    }

    if (!hideBurnHours) {
      series.push({
        id: "predictedBurnHoursCumulative",
        dataKey: "predictedBurnHoursCumulative",
        type: "line",
        label: `${t("Predicted Burn Hours")}, ${t("cumulative")}`,
        color: blue["200"],
        yAxisId: "burnHours",
        valueFormatter: (v) => v?.format(numberFormat) ?? null,
      } as LineSeriesType);
    }

    if (displaySootIndex) {
      series.push({
        id: "predictedSootIndexCumulative",
        dataKey: "predictedSootIndexCumulative",
        type: "line",
        label: `${t("Predicted Soot Index")}, ${t("cumulative")}`,
        yAxisId: "sootIndex",
        color: red["200"],
        valueFormatter: (v) => v?.format(numberFormat) ?? null,
      } as LineSeriesType);
    } else {
      series.push({
        id: "predictedSootingDegreeCumulative",
        dataKey: "predictedSootingDegreeCumulative",
        type: "line",
        label: `${t("Predicted Sooting Degree")}, ${t("cumulative")}`,
        yAxisId: "sootingDegree",
        color: red["200"],
        valueFormatter: roundedPercentageValueFormatter,
      } as LineSeriesType);
    }

    return series;
  }, [displaySootIndex, hideBurnHours, hidePerPeriod, numberFormat, t]);
};

const roundedPercentageValueFormatter = (v: number | null): string | null => {
  return v != null ? `${Math.round(v)} %` : null;
};

const roundedPercentageValueWithOneDecimalFormatter = (
  v: number | null
): string | null => {
  return v != null ? `${v.toFixed(1)} %` : null;
};
